import { useDisclosure } from '@chakra-ui/react';
import { AccountStatusEnum, IAccountRespSchema } from '@monetize/types/app';
import { FilterType, GetListApiFilter } from '@monetize/types/common';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import {
  doActivateAccount,
  doDeactivateAccount,
  useGetAccountList,
} from '../../api/accountsService';
import {
  MButton,
  MFlex,
  MPageContainer,
  MPageHeader,
  MPageSearchInput,
} from '../../components/Monetize';
import DataTableActions from '../../components/Monetize/DataTable/MDataTableActions';
import { DataTable } from '../../components/Monetize/DataTableNew/DataTable';
import { DataTableRef } from '../../components/Monetize/DataTableNew/DataTableTypes';
import { useTableColumns } from '../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { ACTION_COLUMN_KEY } from '../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { ExportEntityButton } from '../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../components/Monetize/RefreshTableButton';
import {
  getAccountDetailRoute,
  getAccountDetailsTabRoute,
} from '../../constants/routes';
import { useShowPastDueFields } from '../../hooks';
import { ACLCheck } from '../../services/acl/ACLCheck';
import { useACL } from '../../services/acl/acl';
import { useDocumentHead } from '../../services/documentHead';
import {
  tableFilterSelector,
  tablePagerSelector,
} from '../../store/global.store';
import { FilterStateKeys, getDefaultPager } from '../../store/store.types';
import {
  buildFilterParamsRequestObject,
  getIsTrulyEmptyList,
  transformTableFilterValue,
} from '../../utils';
import { navigateOrOpenNewWindow } from '../../utils/misc';
import AccountFormModal from './AccountFormModal';
import {
  ACCOUNT_LIST_CATALOG,
  AccountListColumnContext,
} from './accountList.columns';

const AccountsListPage = () => {
  // START useState
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [initialFilters, persistTableFilter] = useAtom(
    tableFilterSelector(FilterStateKeys.ACCOUNT_LIST),
  );

  const [filters, setFilters] = useState<FilterType[]>(initialFilters);
  const [searchKey] = useState<string>('query');
  const [searchTerm, setSearchTerm] = useState<string>(
    searchParams.get('search') ?? '',
  );
  const filterParams = useMemo<GetListApiFilter>(
    () =>
      buildFilterParamsRequestObject(
        transformTableFilterValue(filters),
        searchTerm,
        searchKey,
        'equals',
      ),
    [filters, searchTerm, searchKey],
  );
  const [pager, setPager] = useAtom(
    tablePagerSelector(FilterStateKeys.ACCOUNT_LIST),
  );
  const { canDo } = useACL();
  const showPastDue = useShowPastDueFields();
  const tableRef = useRef<DataTableRef>(null);

  useEffect(() => {
    persistTableFilter(filters);
  }, [filters, persistTableFilter]);

  const {
    isLoading,
    isRefetching,
    isFetched,
    data: accountList,
    refetch,
  } = useGetAccountList({
    config: pager,
    filters: filterParams,
    /** If user pressed enter on the table search, we need to use the table search API */
    useSearchApi: !!searchTerm,
  });

  const {
    open: isCreateModalOpen,
    onOpen: onOpenCreateModal,
    onClose: onCloseCreateModal,
  } = useDisclosure();
  // END useState

  useEffect(() => {
    setSearchTerm(searchParams.get('search') ?? '');
  }, [searchParams]);

  const { setDocTitle } = useDocumentHead();
  useEffect(() => {
    setDocTitle('Accounts');
  }, []);

  const onResetFilter = () => {
    setFilters([]);
    setPager(getDefaultPager(FilterStateKeys.ACCOUNT_LIST));

    tableRef.current?.resetFilters();
  };

  const handleDeactivateAccount = useCallback(
    async (rowData: IAccountRespSchema) => {
      await doDeactivateAccount(rowData.id);
      refetch(); // TODO: we already have the new data, we can update the cache instead of a full re-fetch
    },
    [refetch],
  );

  const handleActivateAccount = useCallback(
    async (rowData: IAccountRespSchema) => {
      await doActivateAccount(rowData.id);
      refetch(); // TODO: we already have the new data, we can update the cache instead of a full re-fetch
    },
    [refetch],
  );

  const actionBodyTemplate = useCallback(
    (rowData: IAccountRespSchema) => {
      const { id, accountName, status } = rowData;
      const confirmProps = {
        title: `Are you sure you want to deactivate "${accountName}"?`,
        description: 'Once deactivated, this account will no longer be billed.',
        yesButton: 'Deactivate',
      };
      const actions = [
        {
          title: 'Edit',
          enabled: canDo([['accounts', 'update']]),
          action: () => navigate(getAccountDetailsTabRoute(id)),
        },
        {
          title: 'Activate',
          enabled:
            canDo([['accounts', 'update']]) &&
            [AccountStatusEnum.INACTIVE, AccountStatusEnum.CANCELED].includes(
              status,
            ),
          action: () => handleActivateAccount(rowData),
        },
        {
          title: 'Deactivate',
          color: 'danger',
          confirmProps: {
            ...confirmProps,
            yesButton: 'Deactivate',
          },
          enabled:
            canDo([['accounts', 'update']]) &&
            status === AccountStatusEnum.ACTIVE,
          action: () => handleDeactivateAccount(rowData),
        },
      ];

      return <DataTableActions actions={actions} />;
    },
    [canDo, navigate, handleActivateAccount, handleDeactivateAccount],
  );

  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading: isLoading,
    totalElements: accountList?.totalElements || 0,
    filters,
    searchTerm,
    page: pager.page,
  });

  const columnContext = useMemo<AccountListColumnContext>(
    () => ({ actionBodyTemplate, showPastDue }),
    [actionBodyTemplate, showPastDue],
  );
  const { columns, state: columnState } = useTableColumns(
    ACCOUNT_LIST_CATALOG,
    columnContext,
  );

  return (
    <MPageContainer enableAccountSearch>
      <MPageHeader title="Accounts">
        <MFlex>
          <MPageSearchInput
            placeholderKey="Account Name"
            value={searchTerm}
            count={accountList?.totalElements}
            mode="alwaysExpanded"
            onChange={setSearchTerm}
          />
          {!isTrulyEmptyList && (
            <>
              <RefreshTableButton refetch={refetch} />
              <ExportEntityButton
                entity="accounts"
                filters={filters}
                searchKey={searchKey}
                searchTerm={searchTerm}
                sortField={pager.sortField}
                sortOrder={pager.sortOrder}
              />
            </>
          )}

          <ACLCheck acls={[['accounts', 'create']]}>
            <MButton
              variant="primary"
              ml="2"
              onClick={onOpenCreateModal}
              data-testid="create-account-btn"
            >
              New
            </MButton>
          </ACLCheck>
        </MFlex>
      </MPageHeader>
      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={accountList?.content ?? []}
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={accountList?.totalElements}
        totalPages={accountList?.totalPages}
        pager={pager}
        setPager={setPager}
        initialFilters={filters}
        onFiltersChange={setFilters}
        onRowClick={(e, _tableRow, openInNewWindow) =>
          navigateOrOpenNewWindow(
            navigate,
            getAccountDetailRoute(e.id),
            openInNewWindow,
          )
        }
        isLoading={isLoading || (isRefetching && !isFetched)}
        emptyProps={{
          mainMessage: 'Looks like there are no accounts here.',
          smallMessage:
            "To get started, you'll need to create an account first.",
          btnLabel: canDo([['accounts', 'create']]) ? 'New Account' : '',
          onClick: onOpenCreateModal,
        }}
        emptyFilterResultsProps={{
          btnLabel: 'Reset Filters',
        }}
        resetFilter={onResetFilter}
      />

      <AccountFormModal
        isOpen={isCreateModalOpen}
        onClose={onCloseCreateModal}
      />
    </MPageContainer>
  );
};

export default AccountsListPage;
